/**
 * FailedPaymentRetryModal — quick path to fix a past-due payment (Screen 7).
 *
 * Shown for `past_due` users (from the DunningBanner CTA or a past-due invoice
 * row). Explains that we'll open Stripe's secure payment page so the user can
 * update their card, after which Stripe auto-retries the charge. Primary CTA →
 * `useCustomerPortal().openPortal()` → redirect to the Customer Portal.
 *
 * Reuses the shared `ui/Modal.js` primitive (focus trap, ESC close, scroll
 * lock, `role="dialog"`). The copy explicitly names "Stripe's secure payment
 * page" so the redirect doesn't feel like a phishing handoff (design Screen 7).
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the modal is shown.
 * @param {() => void} props.onClose - Dismiss handler ("Cancel" / ESC / ×).
 * @param {string} [props.planName='your plan'] - Plan name for the copy.
 */

import React from 'react';
import Modal from '../ui/Modal';
import { useCustomerPortal } from '../../hooks/useCustomerPortal';
import styles from './FailedPaymentRetryModal.module.css';

export default function FailedPaymentRetryModal({
  open,
  onClose,
  planName = 'your plan',
}) {
  const { openPortal, loading, error } = useCustomerPortal();

  const handleOpenPortal = () => {
    openPortal();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Update your payment"
      size="sm"
      footer={
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.secondary}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.primary}
            onClick={handleOpenPortal}
            disabled={loading}
            aria-busy={loading}
            data-testid="retry-payment-open-portal"
          >
            {loading ? 'Opening Stripe…' : 'Open secure payment page'}
          </button>
        </div>
      }
    >
      <div data-testid="retry-payment-modal">
        <p className={styles.body}>
          We'll open Stripe's secure payment page so you can update your card.
          Once it's updated, we'll retry the charge automatically and restore{' '}
          {planName}.
        </p>

        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
